import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { DAYS } from "@/components/BestTimes";

export const fmtDublin = (iso) => { const d = new Date(iso); return d.toLocaleString("en-IE", { timeZone: "Europe/Dublin", weekday: "short", day: "numeric", month: "short", ...(d.getFullYear() !== new Date().getFullYear() && { year: "numeric" }), hour: "2-digit", minute: "2-digit" }); };

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const nextDow = (dow) => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + (((dow - d.getDay() + 7) % 7) || 7)); return d; };
const tomorrow = () => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1); return d; };
const today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

export function ScheduleDialog({ ids, open, onOpenChange, onDone }) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("09:00");
  const [best, setBest] = useState(null);
  useEffect(() => {
    if (!open) return;
    setDate(nextDow(2)); setTime("09:00");
    api.get("/analytics/best-times").then((r) => setBest(r.data.best)).catch(() => setBest(null));
  }, [open]);
  const go = async (clear) => {
    try {
      const { data } = await api.post("/outbox/schedule", clear ? { ids, clear: true } : { ids, date: ymd(date), time });
      toast.success(clear ? `Schedule cleared for ${data.updated} email(s)` : `${data.updated} email(s) scheduled for ${fmtDublin(data.scheduled_at)}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const quick = [["Tomorrow 9am", tomorrow(), "09:00"], ["Next Tue 9am", nextDow(2), "09:00"], ["Next Mon 9am", nextDow(1), "09:00"]];
  if (best) quick.unshift([`Best time · ${DAYS[best.weekday]} ${String(best.hour).padStart(2, "0")}:00`, nextDow((best.weekday + 1) % 7), `${String(best.hour).padStart(2, "0")}:00`, true]);
  const same = (d) => date && ymd(d) === ymd(date);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle>Schedule {ids.length} email(s)</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">{quick.map(([l, d, t, isBest]) => (
          <Button key={l} size="sm" variant={same(d) && time === t ? "default" : "outline"} className={isBest ? "border-orange-300" : ""} onClick={() => { setDate(d); setTime(t); }}
            data-testid={isBest ? "schedule-quick-best" : `schedule-quick-${l.toLowerCase().replace(/\s/g, "-")}`}>{isBest && <Sparkles size={13} className="mr-1 text-orange-500" />}{l}</Button>))}</div>
        <div className="border rounded-lg flex justify-center" data-testid="schedule-calendar">
          <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={{ before: today() }} weekStartsOn={1} />
        </div>
        <div className="flex items-end gap-3">
          <div className="w-36"><div className="label-xs mb-1">Time (Irish)</div><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="schedule-time-input" /></div>
          <div className="text-sm text-slate-600 pb-2" data-testid="schedule-selected">{date ? `${date.toLocaleDateString("en-IE", { weekday: "long", day: "numeric", month: "long" })} at ${time}` : "Pick a date"}</div>
        </div>
        <p className="text-xs text-slate-500">Times are Irish time (Europe/Dublin). Emails go out at the next automation run after this time, usually within 15 minutes. You can still send them sooner by hand.</p>
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={() => go(true)} data-testid="schedule-clear-btn">Clear schedule</Button>
          <Button disabled={!date || !time} onClick={() => go(false)} data-testid="schedule-confirm-btn">Schedule</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
